"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Calendar, MapPin, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
export default function LocalBridge() {
  const shouldReduceMotion = useReducedMotion();
  const benefits = ["Book a direct consultation with Yassine Essaadi — no agency middleman", "Tailored local map pack and organic geographic rankings", "Clear monthly reporting with transparent search metrics"];
  return <section id="localseobridgecta" data-nav="light" className="relative py-20 md:py-28 bg-background text-foreground overflow-hidden">
      {/* Decorative subtle background accents */}
      <div className="absolute inset-0 pointer-events-none -z-10 flex justify-center">
        <div className="w-full max-w-7xl h-full relative">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[350px] bg-secondary/35 rounded-full blur-3xl" />
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="max-w-4xl mx-auto">
          <Card className="border border-border/80 bg-card text-card-foreground shadow-lg rounded-2xl overflow-hidden">
            <CardContent className="p-8 sm:p-12 md:p-14">
              <div className="flex flex-col items-center text-center space-y-6">
                <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold rounded-full border border-border flex items-center gap-1.5">
                  <MapPin className="size-3.5 text-primary" />
                  <span>Local Search Expertise</span>
                </Badge>

                <div className="space-y-4 max-w-2xl">
                  <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground font-serif">
                    Ready to win customers in your local area?
                  </h2>
                  <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
                    Book a free consultation with me to assess your local search visibility, review regional map pack rankings, and plan your growth.
                  </p>
                </div>

                {/* Consultant promises list */}
                <div className="w-full max-w-xl pt-2 pb-4">
                  <ul className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-left">
                    {benefits.map((benefit, i) => <li key={i} data-index={i} className="flex items-start gap-2 text-xs sm:text-sm text-muted-foreground bg-muted/40 p-3 rounded-lg border border-border/50">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                        <span className="leading-snug">{benefit}</span>
                      </li>)}
                  </ul>
                </div>

                {/* Primary & Secondary CTAs */}
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4 w-full sm:w-auto pt-2">
                  <Button asChild size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 font-medium px-8 py-3 rounded-md shadow-sm transition-[color,background-color,box-shadow]">
                    <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                      <SafeHydrate><Calendar className="size-4" /></SafeHydrate>
                      <span>Book a Free SEO Consultation</span>
                    </Link>
                  </Button>

                  <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border-border text-foreground hover:bg-muted font-medium px-6 py-3 rounded-md transition-[color,background-color,border-color]">
                    <Link to="/LocalSEOPage" className="inline-flex items-center justify-center gap-2">
                      <span>Explore Full Local SEO Services</span>
                      <ArrowRight className="size-4" />
                    </Link>
                  </Button>
                </div>

                <p className="text-xs text-muted-foreground">
                  Personal audit and initial call with Yassine Essaadi. No obligation, no sales team.
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>;
}